import { WidgetControlType } from '@examdojo/core/widget';
import type { GraphpadWidgetConfig } from '../graphpad.model';

/**
 * Preset for testing cosine functions.
 * Focus: Cosine wave with extrema, roots, and periodic properties.
 */
export const COSINE_PRESET: GraphpadWidgetConfig = {
  id: 'cosine',
  type: 'graphpad',
  params: {
    f: {
      type: 'cosine',
      params: { a: 1, b: 1, c: 2, d: 0 },
    },
    'f-extrema': {
      type: 'extrema',
      params: { of: 'f' },
    },
    'f-roots': {
      type: 'roots',
      params: { of: 'f' },
    },
    'f-amplitude': {
      type: 'amplitude',
      params: { of: 'f' },
    },
    'f-period': {
      type: 'period',
      params: { of: 'f' },
    },
    'f-phaseShift': {
      type: 'phaseShift',
      params: { of: 'f' },
    },
  },
  display: {
    coordinateSystem: {
      boundingbox: { xStart: -Math.PI / 2, xEnd: (7 * Math.PI) / 2, yStart: -3.5, yEnd: 2.5 },
      grid: { type: 'lines', visible: true },
      axes: [
        {
          id: 'x',
          orientation: 'horizontal',
          position: 0,
          isPrimary: true,
          drawGridLines: true,
          ticks: {
            ticksDistance: Math.PI / 2,
            labelFormat: 'pi',
          },
        },
        {
          id: 'y',
          orientation: 'vertical',
          position: 0,
          isPrimary: true,
          drawGridLines: true,
          ticks: { ticksDistance: 1 },
        },
      ],
    },
    elements: {
      'f-extrema': {
        subparts: {
          maxima: { point: { label: { text: 'max', position: 'topRight' } } },
          minima: { point: { label: { text: 'min', position: 'bottomRight' } } },
        },
      },
      'f-amplitude': {
        subparts: {
          top: {
            line: {
              label: { text: 'A = {length}', position: 'right', variant: 'secondary' },
            },
          },
        },
      },
      'f-period': {
        subparts: {
          arrow: {
            line: {
              label: { text: 'T = {length}', position: 'top', variant: 'secondary' },
            },
          },
        },
      },
      'f-phaseShift': {
        subparts: {
          referenceLine: { visible: false },
          arrow: {
            line: {
              label: { text: 'c = {length}', position: 'top', variant: 'secondary' },
            },
          },
        },
      },
    },
  },
  behavior: {
    controls: [
      {
        id: 'slider-a',
        type: WidgetControlType.Slider,
        label: 'a (amplitude)',
        param: 'f.a',
        range: [-3, 3, 0.1],
        defaultValue: 1,
        editable: true,
        color: 'sky',
      },
      {
        id: 'slider-b',
        type: WidgetControlType.Slider,
        label: 'b (frequency)',
        param: 'f.b',
        range: [0.1, 3, 0.1],
        defaultValue: 1,
        editable: true,
        color: 'indigo',
      },
      {
        id: 'slider-c',
        type: WidgetControlType.Slider,
        label: 'c (phase)',
        param: 'f.c',
        range: [-Math.PI, Math.PI, 0.1],
        defaultValue: 0,
        editable: true,
        color: 'green',
      },
      {
        id: 'slider-d',
        type: WidgetControlType.Slider,
        label: 'd (y-shift)',
        param: 'f.d',
        range: [-2, 2, 0.1],
        defaultValue: 0,
        editable: true,
        color: 'amber',
      },
    ],
  },
};
